import { useEffect, useId, useMemo, useState } from "react";
import { fuzzyScore, type Command, type CommandRegistry } from "@helm/core";
import { TRADING_DISABLED_REASON } from "@helm/ui";

/**
 * Ctrl/Cmd+K command palette (SPEC §17.5): fuzzy search over the registry, recent first, shows
 * each command's hotkey so it teaches shortcuts. Commands that exist but are unavailable (trading,
 * until the order core ships — A2) are listed greyed out with the reason and cannot run.
 */
export function CommandPalette({
  registry,
  open,
  onClose,
  unavailable = [],
}: {
  registry: CommandRegistry;
  open: boolean;
  onClose: () => void;
  unavailable?: readonly Command[];
}): React.JSX.Element | null {
  const listId = useId();
  const [query, setQuery] = useState("");
  const [index, setIndex] = useState(0);
  const results: Command[] = useMemo(() => (open ? registry.search(query) : []), [registry, query, open]);
  const disabled: Command[] = useMemo(() => {
    if (!open) return [];
    const q = query.trim().toLowerCase();
    if (q === "") return [];
    return unavailable.filter((c) =>
      // Title, hotkey and the group name only: long descriptions would fuzzy-match almost anything.
      [c.title, c.hotkey ?? "", "trading"].some((t) => Number.isFinite(fuzzyScore(q, t.toLowerCase()))),
    );
  }, [unavailable, query, open]);

  // Reset while rendering the opening frame, not in an effect: an effect ran after the input had
  // already shown the previous query, so keys typed right after Ctrl+K were appended to it
  // ("Open settingsOpen settings") and Enter matched nothing.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setQuery("");
      setIndex(0);
    }
  }

  useEffect(() => {
    setIndex(0);
  }, [query]);

  if (!open) return null;

  const run = (cmd: Command | undefined): void => {
    if (!cmd) return;
    onClose();
    void registry.run(cmd.id, { source: "palette" });
  };

  return (
    <div className="palette-backdrop" onMouseDown={onClose} role="presentation">
      <div
        className="palette"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
      >
        <input
          autoFocus
          className="palette-input"
          placeholder="Type a command…"
          role="combobox"
          aria-expanded="true"
          aria-controls={listId}
          aria-activedescendant={results[index] ? `${listId}-${String(index)}` : undefined}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setIndex((i) => Math.min(i + 1, results.length - 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setIndex((i) => Math.max(i - 1, 0));
            } else if (e.key === "Enter") {
              e.preventDefault();
              // A hover under a still pointer can leave `index` past a list that typing has just
              // narrowed: Enter then runs the first match instead of doing nothing.
              run(results[index] ?? results[0]);
            } else if (e.key === "Escape") {
              onClose();
            }
          }}
        />
        <ul className="palette-list" role="listbox" id={listId} aria-label="Commands">
          {results.length === 0 && disabled.length === 0 && (
            <li className="palette-empty muted">No matching commands</li>
          )}
          {results.map((c, i) => (
            <li
              key={c.id}
              id={`${listId}-${String(i)}`}
              role="option"
              aria-selected={i === index}
              className={i === index ? "palette-item active" : "palette-item"}
              onMouseEnter={() => {
                setIndex(i);
              }}
              onClick={() => {
                run(c);
              }}
            >
              <span className="palette-group">{c.group}</span>
              <span className="palette-title">{c.title}</span>
              {c.hotkey && <kbd className="palette-key">{c.hotkey}</kbd>}
            </li>
          ))}
          {disabled.map((c) => (
            <li
              key={c.id}
              role="option"
              aria-selected={false}
              aria-disabled="true"
              className="palette-item disabled"
              title={TRADING_DISABLED_REASON}
            >
              <span className="palette-group">{c.group}</span>
              <span className="palette-title">
                {c.title}
                <span className="palette-reason"> — {TRADING_DISABLED_REASON}</span>
              </span>
              {c.hotkey && <kbd className="palette-key">{c.hotkey}</kbd>}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
